import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Form } from 'antd';
import { Modal as UiKitModal, DatePicker, InputNumber, RangePicker } from '@gamesb42/ui-kit';
import { Moment } from 'moment';

import getTransactionErrorMessage from 'helpers/getTransactionErrorMessage';

import {
  BulkActionPayloadT,
  BulkAoActionT,
  MAX_GRACE_PERIOD,
  MAX_PAYMENT_DAY,
  MIN_GRACE_PERIOD,
  MIN_PAYMENT_DAY,
  executeBulkAction,
} from './bulkActions';

const DATE_FORMAT = 'DD.MM.YYYY';
const API_DATE_FORMAT = 'YYYY-MM-DD';

type FormValuesT = {
  date?: Moment;
  period?: [Moment, Moment];
  grace_period?: number;
  payment_day?: number;
  apply_from_date?: Moment;
};

type FieldKindT = 'date' | 'period' | 'gracePeriod' | 'paymentDay';

const ACTION_FIELDS: Record<BulkAoActionT, { kind: FieldKindT; labelKey: string }> = {
  CHANGE_REPAYMENT_DAY: { kind: 'date', labelKey: 'forms.activationOrder.repaymentDate' },
  TURN_OFF_CAPITALIZATION_FOR_PERIOD: { kind: 'period', labelKey: 'forms.activationOrder.bulk.selectPeriod' },
  CHANGE_GRACE_PERIOD: { kind: 'gracePeriod', labelKey: 'forms.activationOrder.gracePeriod' },
  TURN_OFF_COMMISSION: { kind: 'date', labelKey: 'forms.activationOrder.commissionDisableDate' },
  CHANGE_PAYMENT_DAY: { kind: 'paymentDay', labelKey: 'forms.activationOrder.bulk.fields.paymentDay' },
  TURN_OFF_CAPITALIZATION_UNTIL_END_OF_AO: {
    kind: 'date',
    labelKey: 'forms.activationOrder.capitalizationDisableDate',
  },
  TURN_OFF_DEFAULT_INTEREST: { kind: 'date', labelKey: 'forms.activationOrder.defaultInterestDisableDate' },
  TURN_OFF_DEFAULT_INTEREST_FOR_PERIOD: { kind: 'period', labelKey: 'forms.activationOrder.bulk.selectPeriod' },
};

const buildPayload = (action: BulkAoActionT, orderIds: string[], values: FormValuesT): BulkActionPayloadT => {
  const date = values.date?.format(API_DATE_FORMAT) as string;
  const [dateStart, dateEnd] = values.period || [];

  switch (action) {
    case 'CHANGE_REPAYMENT_DAY':
      return { action, order_ids: orderIds, params: { repayment_date: date } };
    case 'TURN_OFF_COMMISSION':
      return { action, order_ids: orderIds, params: { commission_disable_date: date } };
    case 'TURN_OFF_CAPITALIZATION_UNTIL_END_OF_AO':
      return { action, order_ids: orderIds, params: { capitalization_disable_date: date } };
    case 'TURN_OFF_DEFAULT_INTEREST':
      return { action, order_ids: orderIds, params: { default_interest_disable_date: date } };
    case 'TURN_OFF_CAPITALIZATION_FOR_PERIOD':
      return {
        action,
        order_ids: orderIds,
        params: { date_start: dateStart.format(API_DATE_FORMAT), date_end: dateEnd.format(API_DATE_FORMAT) },
      };
    case 'TURN_OFF_DEFAULT_INTEREST_FOR_PERIOD':
      return {
        action,
        order_ids: orderIds,
        params: {
          di_period_disabled: [
            { date_start: dateStart.format(API_DATE_FORMAT), date_end: dateEnd.format(API_DATE_FORMAT) },
          ],
        },
      };
    case 'CHANGE_GRACE_PERIOD':
      return { action, order_ids: orderIds, params: { grace_period: Number(values.grace_period) } };
    case 'CHANGE_PAYMENT_DAY':
    default:
      return {
        action: 'CHANGE_PAYMENT_DAY',
        order_ids: orderIds,
        params: {
          payment_days: [
            {
              payment_day: Number(values.payment_day),
              apply_from_date: values.apply_from_date?.format(API_DATE_FORMAT) as string,
            },
          ],
        },
      };
  }
};

type PropsT = {
  companyId: string;
  orderIds: string[];
  activeAction: BulkAoActionT | null;
  title: string;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmittingChange: (value: boolean) => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
};

const AOBulkActionModals: FC<PropsT> = ({
  companyId,
  orderIds,
  activeAction,
  title,
  isSubmitting,
  onClose,
  onSubmittingChange,
  onSuccess,
  onError,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<FormValuesT>();
  const field = activeAction ? ACTION_FIELDS[activeAction] : null;

  const closeModal = () => {
    form.resetFields();
    onClose();
  };

  const handleSubmit = () =>
    form.validateFields().then((values) => {
      if (!activeAction) return;

      onSubmittingChange(true);

      executeBulkAction(companyId, buildPayload(activeAction, orderIds, values))
        .then((response) => {
          onSuccess(
            t('forms.activationOrder.bulk.applySuccess', {
              setting: title,
              count: response?.processed ?? orderIds.length,
            }),
          );
          closeModal();
        })
        .catch((error: Error & { status?: number }) => {
          onError(
            getTransactionErrorMessage(
              { status: error?.status, text: error?.message },
              t('forms.activationOrder.bulk.executeError'),
            ),
          );
        })
        .finally(() => onSubmittingChange(false));
    });

  return (
    <UiKitModal
      open={Boolean(activeAction)}
      width={600}
      title={title}
      okText={t('forms.activationOrder.bulk.apply')}
      cancelText={t('forms.activationOrder.cancel')}
      confirmLoading={isSubmitting}
      okButtonProps={{}}
      onOk={handleSubmit}
      onCancel={closeModal}
      destroyOnClose
    >
      <Form form={form} layout="vertical" preserve={false}>
        {field?.kind === 'date' && (
          <Form.Item name="date" rules={[{ required: true, message: t('forms.activationOrder.bulk.dateRequired') }]}>
            <DatePicker label={t(field.labelKey)} size="large" format={DATE_FORMAT} />
          </Form.Item>
        )}
        {field?.kind === 'period' && (
          <Form.Item
            name="period"
            rules={[{ required: true, message: t('forms.activationOrder.bulk.periodRequired') }]}
          >
            <RangePicker label={t(field.labelKey)} size="large" format={DATE_FORMAT} />
          </Form.Item>
        )}
        {field?.kind === 'gracePeriod' && (
          <Form.Item
            name="grace_period"
            rules={[
              {
                required: true,
                type: 'number',
                min: MIN_GRACE_PERIOD,
                max: MAX_GRACE_PERIOD,
                message: t('forms.activationOrder.bulk.gracePeriodRange', {
                  min: MIN_GRACE_PERIOD,
                  max: MAX_GRACE_PERIOD,
                }),
              },
            ]}
          >
            <InputNumber
              label={t(field.labelKey)}
              size="large"
              width="100%"
              min={MIN_GRACE_PERIOD}
              max={MAX_GRACE_PERIOD}
              precision={0}
            />
          </Form.Item>
        )}
        {field?.kind === 'paymentDay' && (
          <>
            <Form.Item
              name="payment_day"
              rules={[
                {
                  required: true,
                  type: 'number',
                  min: MIN_PAYMENT_DAY,
                  max: MAX_PAYMENT_DAY,
                  message: t('forms.activationOrder.bulk.paymentDayRange', {
                    min: MIN_PAYMENT_DAY,
                    max: MAX_PAYMENT_DAY,
                  }),
                },
              ]}
            >
              <InputNumber
                label={t(field.labelKey)}
                size="large"
                width="100%"
                min={MIN_PAYMENT_DAY}
                max={MAX_PAYMENT_DAY}
                precision={0}
              />
            </Form.Item>
            <Form.Item
              name="apply_from_date"
              rules={[{ required: true, message: t('forms.activationOrder.bulk.dateRequired') }]}
              extra={t('forms.activationOrder.bulk.applyFromDateHint')}
            >
              <DatePicker
                label={t('forms.activationOrder.bulk.fields.applyFromDate')}
                size="large"
                format={DATE_FORMAT}
              />
            </Form.Item>
          </>
        )}
      </Form>
    </UiKitModal>
  );
};

export default AOBulkActionModals;
